/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  SEASON RULES — hard coded on purpose
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *  Every fixed, calendar-dependent booking rule lives in this file. These are
 *  yearly constants from the customer, so they are deliberately NOT editable
 *  in the WP admin — except the per-location switches and the number of usable
 *  BBQ donuts, which are in BBQ-Optionen → Winter (Nov–März).
 *
 *  Year at a glance
 *  ────────────────
 *
 *    Jan  Feb  Mar │ Apr  May  Jun  Jul  Aug  Sep  Oct │ Nov  Dec
 *    ─ WINTER ─ SUM │ ───────────── SUMMER ───────────── │ ─ WINTER ─
 *    └ Jan–Mar only if opened per location in the admin
 *                  │                       ▲──────────────┤
 *                  │   Sep 8th – Oct 31: no slots at 19:00+ │ Nov/Dec: 19:00 open
 *
 *   JAN–MAR  Closed, unless opened per location in the WP admin. Then: the
 *            normal times from BBQ-Optionen → Zeiten und Tage, capacity
 *            limited to the usable BBQ donuts (maxPeoplePerSlot).
 *            Jan + Feb count as WINTER (winter packages), March as SUMMER.
 *   SUMMER   Packages come from the WP admin (BBQ-Optionen → Pakete &
 *            Ausstattung), incl. the Mon–Wed "Gruppenangebot".
 *   WINTER   Nov – Feb (WINTER_MONTHS). Fixed winter packages
 *            (WINTER_PACKAGES below), drinks hint instead of the free-text field.
 *            Capacity limited to the usable BBQ donuts (maxPeoplePerSlot).
 *            Evening slots (19:00+) bookable (EVENING_ALLOWED_MONTHS).
 *            Single locations can be closed for Nov/Dec in the WP admin.
 *            Boat flat rates stay exactly the same as in summer.
 *
 *  Months below are 0-based, like JavaScript's Date (0 = Jan … 11 = Dec).
 */

import type { AppData } from './App';

// ─── Bookable period ────────────────────────────────────────────────────────

/** First month of the regular season: April */
export const MIN_MONTH = 3;

/** Last month of the regular season: December */
export const MAX_MONTH = 11;

/** January – March: closed unless opened per location in the WP admin */
export const JAN_MAR_MONTHS: readonly number[] = [0, 1, 2];

/** November + December: open unless closed per location in the WP admin */
export const NOV_DEC_MONTHS: readonly number[] = [10, 11];

export type Location = 'hanekenfaehr' | 'alter_hafen';

/** Location switched off for Nov/Dec in BBQ-Optionen → Winter (Nov–März) */
export function isLocationClosedInWinter(
  loc: Location,
  appData: AppData
): boolean {
  return (appData.winterDisabledLocations ?? []).includes(loc);
}

/** Location opened for Jan–Mar in BBQ-Optionen → Winter (Nov–März) */
export function isLocationOpenJanMar(loc: Location, appData: AppData): boolean {
  return (appData.janMarEnabledLocations ?? []).includes(loc);
}

/** Whether a month (0 = Jan) can be booked at all for a location. */
export function isMonthBookable(
  month: number,
  loc: Location,
  appData: AppData
): boolean {
  if (JAN_MAR_MONTHS.includes(month)) return isLocationOpenJanMar(loc, appData);
  if (NOV_DEC_MONTHS.includes(month))
    return !isLocationClosedInWinter(loc, appData);
  return month >= MIN_MONTH && month <= MAX_MONTH;
}

/** Whether a day can be booked at all for a location. */
export function isDateBookable(
  date: Date,
  loc: Location,
  appData: AppData
): boolean {
  return isMonthBookable(date.getMonth(), loc, appData);
}

/**
 * Calendar arrows: the next (dir = 1) or previous (dir = -1) month that is
 * bookable for the location, skipping closed months.
 */
export function stepBookableMonth(
  year: number,
  month: number,
  dir: 1 | -1,
  loc: Location,
  appData: AppData
): { year: number; month: number } {
  let y = year;
  let m = month;
  for (let i = 0; i < 12; i++) {
    m += dir;
    if (m > 11) ((m = 0), y++);
    if (m < 0) ((m = 11), y--);
    if (isMonthBookable(m, loc, appData)) return { year: y, month: m };
  }
  // nothing bookable at all — just move one month
  return month + dir > 11
    ? { year: year + 1, month: 0 }
    : month + dir < 0
      ? { year: year - 1, month: 11 }
      : { year, month: month + dir };
}

// ─── Seasons ────────────────────────────────────────────────────────────────

export type Season = 'summer' | 'winter';

/**
 * Winter package season: November – February (Christmas offers stay available
 * in Jan + Feb). March is back to the normal summer packages.
 */
export const WINTER_MONTHS: readonly number[] = [10, 11, 0, 1];

/**
 * Season of a booking date. Accepts the form's ISO date string ("2026-11-14")
 * and parses it as a *local* date, so the month can't shift across midnight
 * in other time zones.
 */
export function getSeason(date: Date | string | undefined): Season {
  if (!date) return 'summer';
  return WINTER_MONTHS.includes(toLocalDate(date).getMonth())
    ? 'winter'
    : 'summer';
}

function toLocalDate(date: Date | string): Date {
  if (date instanceof Date) return date;
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
}

// ─── Evening blackout ───────────────────────────────────────────────────────
// "Later than September 7th" no slots from 19:00 on — except in the months
// listed in EVENING_ALLOWED_MONTHS (November only). The cutoff is
// computed per calendar year, so evening slots are back in April anyway.

export const EVENING_BLACKOUT_TIME = '19:00';
const EVENING_BLACKOUT_MONTH = 8; // September
const EVENING_BLACKOUT_DAY = 8; // blocked FROM Sep 8th on

/** Months where 19:00+ slots are bookable despite the blackout: Nov + Dec */
export const EVENING_ALLOWED_MONTHS: readonly number[] = [10, 11];

/** `time` is "HH:MM" (zero-padded), as configured in BBQ-Optionen → Zeiten. */
export function isTimeBlockedBySeason(date: Date, time: string): boolean {
  if (EVENING_ALLOWED_MONTHS.includes(date.getMonth())) return false;

  const cutoff = new Date(
    date.getFullYear(),
    EVENING_BLACKOUT_MONTH,
    EVENING_BLACKOUT_DAY
  );
  return date >= cutoff && time >= EVENING_BLACKOUT_TIME;
}

// ─── Packages ───────────────────────────────────────────────────────────────

export interface SeasonPackage {
  /** Form field key → `packages.<key>` */
  key: string;
  /** Display name — this is also what's stored in the booking & e-mail */
  name: string;
  /** Price in € */
  price: number;
  /** true = per person, false = per boat */
  perPerson: boolean;
  /** Section in the configurator */
  group: 'equipment' | 'optional' | 'drinks' | 'food';
}

// ─── Capacity Nov – Mar ─────────────────────────────────────────────────────
// Only the BBQ donuts with the dome may be used then. Their number is set in
// BBQ-Optionen → Winter (Nov–März): 1 donut = max. 10 persons per time slot,
// 2 donuts = max. 20. Caps the `?personen=` capacity.

/** Persons per BBQ donut */
export const DONUT_CAPACITY = 10;

/** Months with limited capacity: Nov, Dec, Jan, Feb, Mar */
export const LIMITED_CAPACITY_MONTHS: readonly number[] = [10, 11, 0, 1, 2];

/** Usable dome donuts from the WP admin (falls back to 1) */
export function winterDonutCount(appData: AppData): number {
  const n = Math.floor(Number(appData.winterDonutCount ?? 1));
  return n > 0 ? n : 1;
}

/** Person capacity per time slot for a booking date. */
export function maxPeoplePerSlot(
  date: Date | string | undefined,
  people: number,
  appData: AppData
): number {
  if (!date) return people;
  return LIMITED_CAPACITY_MONTHS.includes(toLocalDate(date).getMonth())
    ? Math.min(people, winterDonutCount(appData) * DONUT_CAPACITY)
    : people;
}

/** Max. litres of mulled wine per boat (10 people → max. 1 l per person) */
export const GLUEHWEIN_LITERS_PER_BOAT = 10;

/**
 * Winter packages (November – February). All priced per person.
 * Keys are prefixed `winter_` so they can never collide with admin packages.
 */
export const WINTER_PACKAGES: readonly SeasonPackage[] = [
  {
    key: 'winter_gluehwein',
    name: 'Glühwein-Paket',
    price: 15,
    perPerson: true,
    group: 'drinks',
  },
  {
    key: 'winter_kaesefondue',
    name: 'Käsefondue',
    price: 20,
    perPerson: true,
    group: 'food',
  },
  {
    key: 'winter_schokofondue',
    name: 'Schokoladenfondue mit Obst',
    price: 25,
    perPerson: true,
    group: 'food',
  },
];

/** Shown under "Getränkewunsch" in winter, instead of the free-text field */
export const WINTER_DRINKS_HINT =
  'Weitere Getränke können direkt vor Ort erworben oder mitgebracht werden.';

/** Headlines for the winter package sections in the configurator */
export const WINTER_GROUP_HEADLINES: Record<'drinks' | 'food', string> = {
  drinks: 'Getränke',
  food: 'Essen',
};

/**
 * The packages that can be booked (and are billed) for a season. Summer
 * packages are the admin list, normalised to the same shape — their form key
 * stays the package name, exactly as before.
 */
export function getSeasonPackages(
  season: Season,
  appData: AppData
): SeasonPackage[] {
  if (season === 'winter') return [...WINTER_PACKAGES];

  return appData.packages.map((p) => ({
    key: p.name,
    name: p.name,
    price: +p.price,
    perPerson: p.optional,
    group: p.optional ? 'optional' : 'equipment',
  }));
}

/** Summer-only form keys that aren't admin packages (Mon–Wed Gruppenangebot) */
const SUMMER_EXTRA_KEYS = ['essen'];

/**
 * Form keys belonging to the OTHER season. They get reset when the selected
 * date crosses seasons — react-hook-form and the sessionStorage persist keep
 * values of checkboxes that are no longer shown, so otherwise e.g. a "Grill"
 * ticked on an October date would silently be billed and booked after
 * switching to a November date.
 */
export function packageKeysOutsideSeason(
  season: Season,
  appData: AppData
): string[] {
  return season === 'winter'
    ? [
        ...getSeasonPackages('summer', appData).map((p) => p.key),
        ...SUMMER_EXTRA_KEYS,
      ]
    : WINTER_PACKAGES.map((p) => p.key);
}

/** Price label, e.g. "15 € pro Person" */
export function priceLabel(pkg: SeasonPackage): string {
  const price = pkg.price.toFixed(2).replace('.', ',').replace(',00', '');
  return `${price} € ${pkg.perPerson ? 'pro Person' : 'pro Boot'}`;
}

/** Mulled-wine hint, e.g. "max. 10 Liter pro Boot (bei 17 Personen ca. 1,2 l pro Person)" */
export function gluehweinHint(personCount: number, boatCapacity: number) {
  const base = `max. ${GLUEHWEIN_LITERS_PER_BOAT} Liter pro Boot`;
  if (!personCount || personCount < 1) return base;

  const boats = Math.ceil(personCount / boatCapacity);
  const perPerson = (boats * GLUEHWEIN_LITERS_PER_BOAT) / personCount;
  return `${base} (bei ${personCount} Personen ca. ${perPerson
    .toFixed(1)
    .replace('.', ',')} l pro Person)`;
}
